import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useDarkMode } from "../../contexts/DarkModeContext";
import "./LessonDetails.css";
import { FaFacebookF, FaLinkedinIn, FaTwitter, FaPlay, FaFilePdf, FaStar, FaBook, FaEdit, FaTrash, FaArrowRight, FaPlus } from "react-icons/fa";
import { getLessonDetails, resetLessonDetails } from "../../redux/slices/lessonsSlice";
import { submitLessonRating, clearRatingError, clearRatingSuccess } from "../../redux/slices/ratingsSlice";
import { subscribeWithTeacher, clearSubscriptionError, clearSubscriptionSuccess } from "../../redux/slices/subscriptionSlice";
import { fetchLessonDuties, clearDutiesError } from "../../redux/slices/lessonDutiesSlice";
import { toast } from "react-toastify";

function LessonDetails() {
  const { isDarkMode } = useDarkMode();
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  


  const { lessonDetails, lessonDetailsLoading, lessonDetailsError } = useSelector((state) => state.lessons);
  const { isSubmitting, submitError, submitSuccess } = useSelector((state) => state.ratings);
  const { isSubscribing, subscriptionError, subscriptionSuccess, paymentUrl } = useSelector((state) => state.subscription);
  const { duties, loading: dutiesLoading, error: dutiesError } = useSelector((state) => state.lessonDuties);
  const { currentUser, profileData } = useSelector((state) => state.auth);
  
  const [showVideo, setShowVideo] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);
  const [editForm, setEditForm] = useState({
    title_ar: '',
    title_en: '',
    description_ar: '',
    description_en: '',
    video_link: '',
    video_type: 'offline',
    free_status: 0,
    day: '',
    time: '',
    image: null,
    files: []
  });
  
  // متغيرات مودال عرض الواجب للمعلم
  const [showDutyModal, setShowDutyModal] = useState(false);
  const [selectedDuty, setSelectedDuty] = useState(null);

  const userRole = currentUser?.role || localStorage.getItem("userRole");
  const isTeacher = userRole === "teacher";
  const isStudent = userRole === "student";

  // Handle browser back button and navigation
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Clear lesson details when user navigates away
      dispatch(resetLessonDetails());
    };

    const handlePopState = () => {
      // Handle browser back/forward buttons
    dispatch(resetLessonDetails());
    };

    // Add to browser history to ensure proper navigation
    if (window.history.state === null) {
      window.history.replaceState({ id: id }, '', window.location.pathname);
    }

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [dispatch, id]);

  // Protection System - Enhanced for better iframe interaction
  useEffect(() => {
    // DevTools / resize detection
    const showBlackout = () => {
      document.body.innerHTML = '';
      document.body.style.background = "black";
      alert("تم حظر المحتوى.");
    };

    function showBlackScreen() {
      document.body.innerHTML = `
        <div style="position:fixed;top:0;left:0;width:100vw;height:100vh;background:black;z-index:99999999"></div>
      `;
    }

    // Monitor for screen recording attempts
    let suspiciousActivity = 0;
    
    // Override dangerous APIs immediately
    try {
      // Block screen capture API
      if (navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia) {
        navigator.mediaDevices.getDisplayMedia = function() {
          showBlackScreen();
          throw new Error('Screen recording blocked');
        };
      }
      
      // Block getUserMedia for screen capture
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        const originalGetUserMedia = navigator.mediaDevices.getUserMedia;
        navigator.mediaDevices.getUserMedia = function(constraints) {
          if (constraints && constraints.video && constraints.video.mediaSource === 'screen') {
            showBlackScreen();
            throw new Error('Screen recording blocked');
          }
          return originalGetUserMedia.call(this, constraints);
        };
      }
      
      // Block legacy getUserMedia
      if (navigator.getUserMedia) {
        navigator.getUserMedia = function() {
          showBlackScreen();
          throw new Error('Screen recording blocked');
        };
      }
      
      // Block webkitGetUserMedia
      if (navigator.webkitGetUserMedia) {
        navigator.webkitGetUserMedia = function() {
          showBlackScreen();
          throw new Error('Screen recording blocked');
        };
      }
      
      // Block mozGetUserMedia
      if (navigator.mozGetUserMedia) {
        navigator.mozGetUserMedia = function() {
          showBlackScreen();
          throw new Error('Screen recording blocked');
        };
      }
    } catch (error) {
      // Ignore errors
    }
    
    const monitorActivity = () => {
      // Skip protection if we're in video interaction mode or edit modal is open
      if (window.videoInteractionMode || window.editModalOpen) {
        // Reset suspicious activity when in video mode
        suspiciousActivity = 0;
        return;
      }
      
      // Check for window focus changes (possible recording software)
      if (document.hasFocus && !document.hasFocus()) {
        suspiciousActivity++;
        if (suspiciousActivity > 5) { // Increased threshold for better video compatibility
          showBlackScreen();
        }
      }
      
      // Reset suspicious activity counter more frequently
      if (suspiciousActivity > 0 && Math.random() < 0.2) {
        suspiciousActivity = Math.max(0, suspiciousActivity - 1);
      }
    };

    // Run monitoring every 100ms
    const monitorInterval = setInterval(monitorActivity, 100);

    // Track Windows key state
    let isWindowsKeyPressed = false;
    let isAltKeyPressed = false;
    let keyPressCount = 0;
    
    const handleKeyUp = (e) => {
      // Windows key released
      if (e.keyCode === 91 || e.keyCode === 92 || e.key === 'Meta' || e.key === 'OS') {
        isWindowsKeyPressed = false;
      }
      // Alt key released
      if (e.keyCode === 18 || e.key === 'Alt') {
        isAltKeyPressed = false;
      }
    };
    
    // Screen recording shortcuts protection
    const handleKeyDown = (e) => {
      if (window.editModalOpen) {
        return;
      }
      // Skip protection for Bootstrap tabs (Tab key navigation)
      if (e.target.closest('[data-bs-toggle="tab"]') && (e.key === 'Tab' || e.key === 'Enter' || e.key === ' ')) {
        return;
      }
      keyPressCount++;
      
      // Track Windows key press
      if (e.keyCode === 91 || e.keyCode === 92 || e.key === 'Meta' || e.key === 'OS') {
        isWindowsKeyPressed = true;
        // Block Windows key immediately
        e.preventDefault();
        if (keyPressCount > 1) {
          showBlackScreen();
          return;
        }
      }
      
      // Track Alt key press
      if (e.keyCode === 18 || e.key === 'Alt') {
        isAltKeyPressed = true;
        // If Windows key is already pressed, block immediately
        if (isWindowsKeyPressed) {
          e.preventDefault();
          showBlackScreen();
          return;
        }
      }
      
      // Block any key combination with Windows + Alt
      if (isWindowsKeyPressed && isAltKeyPressed) {
        e.preventDefault();
        showBlackScreen();
        return;
      }
      // Windows + Shift + R (Windows Game Bar)
      if (e.key === 'R' && e.shiftKey && e.metaKey) {
        e.preventDefault();
        showBlackScreen();
        return;
      }
      
      // Windows + G (Windows Game Bar)
      if (e.key === 'g' && e.metaKey) {
        e.preventDefault();
        showBlackScreen();
        return;
      }
      
      // Windows + Alt + R (Windows Game Bar recording) - Enhanced detection
      if ((e.key === 'R' || e.key === 'r') && e.altKey) {
        // Check if Windows key is pressed using multiple methods
        if (e.metaKey || isWindowsKeyPressed || e.getModifierState?.('OS') || e.getModifierState?.('Win') || e.getModifierState?.('Super')) {
          e.preventDefault();
          showBlackScreen();
          return;
        }
        // Block Alt + R as well (some screen recorders use this)
        e.preventDefault();
        showBlackScreen();
        return;
      }
      
      // Ctrl + Shift + R (Refresh - ALLOWED)
      if (e.key === 'R' && e.ctrlKey && e.shiftKey) {
        // Allow refresh - do nothing
        return;
      }
      
      // Ctrl + R (Refresh - ALLOWED)
      if (e.key === 'r' && e.ctrlKey) {
        // Allow refresh - do nothing
        return;
      }
      
      // F5 (Refresh - ALLOWED)
      if (e.key === 'F5') {
        // Allow refresh - do nothing
        return;
      }
      
      // Windows + Shift + S (Snipping Tool)
      if (e.key === 'S' && e.shiftKey && e.metaKey) {
        e.preventDefault();
        showBlackScreen();
        return;
      }
      
      // Print Screen
      if (e.key === 'PrintScreen') {
        e.preventDefault();
        showBlackScreen();
        return;
      }
      
      // Windows + Print Screen
      if (e.key === 'PrintScreen' && e.metaKey) {
        e.preventDefault();
        showBlackScreen();
        return;
      }
      
      // Alt + Print Screen
      if (e.key === 'PrintScreen' && e.altKey) {
        e.preventDefault();
        showBlackScreen();
        return;
      }
      
      // Ctrl + Shift + I (DevTools)
      if (e.key === 'I' && e.ctrlKey && e.shiftKey) {
        e.preventDefault();
        showBlackout();
        return;
      }
      
      // F12 (DevTools)
      if (e.key === 'F12') {
        e.preventDefault();
        showBlackout();
        return;
      }
      
      // Ctrl + Shift + C (DevTools Elements)
      if (e.key === 'C' && e.ctrlKey && e.shiftKey) {
        e.preventDefault();
        showBlackout();
        return;
      }
      
      // Ctrl + Shift + J (DevTools Console)
      if (e.key === 'J' && e.ctrlKey && e.shiftKey) {
        e.preventDefault();
        showBlackout();
        return;
      }
      
      // Ctrl + U (View Source)
      if (e.key === 'u' && e.ctrlKey) {
        e.preventDefault();
        showBlackout();
        return;
      }
      
      // Ctrl + Shift + U (View Source)
      if (e.key === 'U' && e.ctrlKey && e.shiftKey) {
        e.preventDefault();
        showBlackout();
        return;
      }
      
      // Windows + Shift + C (Xbox Game Bar)
      if (e.key === 'C' && e.shiftKey && e.metaKey) {
        e.preventDefault();
        showBlackScreen();
        return;
      }
      
      // Windows + Alt + G (Xbox Game Bar recording)
      if (e.key === 'G' && e.altKey && e.metaKey) {
        e.preventDefault();
        showBlackScreen();
        return;
      }
      
      // Windows + Alt + R (Xbox Game Bar recording)
      if (e.key === 'R' && e.altKey && e.metaKey) {
        e.preventDefault();
        showBlackScreen();
        return;
      }
      
      // Windows + Alt + B (Xbox Game Bar)
      if (e.key === 'B' && e.altKey && e.metaKey) {
        e.preventDefault();
        showBlackScreen();
        return;
      }
      
      // Ctrl + Shift + E (Chrome DevTools)
      if (e.key === 'E' && e.ctrlKey && e.shiftKey) {
        e.preventDefault();
        showBlackout();
        return;
      }
      
      // Ctrl + Shift + M (Chrome DevTools)
      if (e.key === 'M' && e.ctrlKey && e.shiftKey) {
        e.preventDefault();
        showBlackout();
        return;
      }
      
      // Ctrl + Shift + P (Chrome DevTools)
      if (e.key === 'P' && e.ctrlKey && e.shiftKey) {
        e.preventDefault();
        showBlackout();
        return;
      }
      
      // Ctrl + Shift + K (Firefox DevTools)
      if (e.key === 'K' && e.ctrlKey && e.shiftKey) {
        e.preventDefault();
        showBlackout();
        return;
      }
      
      // Ctrl + Shift + Q (Firefox DevTools)
      if (e.key === 'Q' && e.ctrlKey && e.shiftKey) {
        e.preventDefault();
        showBlackout();
        return;
      }
      
      // Ctrl + Shift + S (Save - ALLOWED)
      if (e.key === 'S' && e.ctrlKey && e.shiftKey) {
        // Allow save - do nothing
        return;
      }
      
      // Ctrl + S (Save - ALLOWED)
      if (e.key === 's' && e.ctrlKey) {
        // Allow save - do nothing
        return;
      }
      
      // Ctrl + A (Select All - ALLOWED)
      if (e.key === 'a' && e.ctrlKey) {
        // Allow select all - do nothing
        return;
      }
      
      // Ctrl + C (Copy - ALLOWED)
      if (e.key === 'c' && e.ctrlKey) {
        // Allow copy - do nothing
        return;
      }
      
      // Ctrl + V (Paste - ALLOWED)
      if (e.key === 'v' && e.ctrlKey) {
        // Allow paste - do nothing
        return;
      }
      
      // Ctrl + X (Cut - ALLOWED)
      if (e.key === 'x' && e.ctrlKey) {
        // Allow cut - do nothing
        return;
      }
      
      // Ctrl + Z (Undo - ALLOWED)
      if (e.key === 'z' && e.ctrlKey) {
        // Allow undo - do nothing
        return;
      }
      
      // Ctrl + Y (Redo - ALLOWED)
      if (e.key === 'y' && e.ctrlKey) {
        // Allow redo - do nothing
        return;
      }
      
      // Right Click Context Menu
      if (e.button === 2) {
        e.preventDefault();
        showBlackout();
        return;
      }
    };

    // Right click protection
    const handleContextMenu = (e) => {
      if (window.editModalOpen) return false;
      e.preventDefault();
      showBlackout();
      return false;
    };

    // Additional protection for context menu
    const handleMouseDown = (e) => {
      if (window.editModalOpen) return false;
      // Skip protection for Bootstrap tabs
      if (e.target.closest('[data-bs-toggle="tab"]')) return false;
      if (e.button === 2) { // Right click
        e.preventDefault();
        showBlackout();
        return false;
      }
    };

    // Enhanced function to check if element is video-related
    const isVideoElement = (element) => {
      if (!element) return false;
      
      try {
        // Extended list of video-related selectors
        const videoSelectors = [
          // Main video containers
          '.video-iframe-container',
          '.youtube-iframe-wrapper', 
          '.vdocipher-iframe-wrapper',
          '.video-file-wrapper',
          '.video-section',
          '.lesson-video',
          '.lesson-video-overlay',
          
          // Video elements
          'video',
          'iframe',
          '.video-iframe',
          
          // Video controls
          '.video-controls',
          '.video-play-icon',
          '.play-pause-btn',
          '.fullscreen-btn',
          '.close-video-btn',
          
          // YouTube specific elements
          '.ytp-chrome-controls',
          '.ytp-progress-bar',
          '.ytp-play-button',
          '.ytp-pause-button',
          '.ytp-volume-slider',
          '.ytp-time-display',
          '.ytp-fullscreen-button',
          '.ytp-settings-button',
          
          // VdoCipher specific elements
          '.vdo-controls',
          '.vdo-play-btn',
          '.vdo-progress',
          '.vdo-volume',
          
          // Any child elements inside video containers
          '.video-iframe-container *',
          '.youtube-iframe-wrapper *',
          '.vdocipher-iframe-wrapper *',
          '.video-file-wrapper *',
          '.video-section *',
          '.lesson-video *'
        ];
        
        // Check element tag name first for performance
        if (element.tagName === 'VIDEO' || element.tagName === 'IFRAME') {
          return true;
        }
        
        // Check if element has video-related classes or IDs
        if (element.className && typeof element.className === 'string') {
          const className = element.className.toLowerCase();
          if (className.includes('video') || 
              className.includes('player') || 
              className.includes('iframe') ||
              className.includes('ytp-') ||
              className.includes('vdo-')) {
            return true;
          }
        }
        
        if (element.id && typeof element.id === 'string') {
          const id = element.id.toLowerCase();
          if (id.includes('video') || 
              id.includes('player') || 
              id.includes('iframe')) {
            return true;
          }
        }
        
        // First check if element itself matches any selector
        const directMatch = videoSelectors.some(selector => {
          try {
            return element.matches && element.matches(selector);
          } catch (error) {
            return false;
          }
        });
        
        if (directMatch) return true;
        
        // Then check if any parent matches
        const parentMatch = videoSelectors.some(selector => {
          try {
            return element.closest && element.closest(selector);
          } catch (error) {
            return false;
          }
        });
        
        if (parentMatch) return true;
        
        // Check if element is inside an iframe
        if (element.closest && element.closest('iframe')) {
          return true;
        }
        
        // Check if element is inside a video element  
        if (element.closest && element.closest('video')) {
          return true;
        }
        
        // Check specific video containers
        const videoContainers = [
          '.video-iframe-container',
          '.youtube-iframe-wrapper',
          '.vdocipher-iframe-wrapper', 
          '.video-file-wrapper',
          '.video-section',
          '.lesson-video'
        ];
        
        return videoContainers.some(container => {
          try {
            return element.closest && element.closest(container);
          } catch (error) {
            return false;
          }
        });
        
      } catch (error) {
        return false;
      }
    };

    // Enhanced function to check if cursor change is from iframe
    const isIframeCursorChange = (cursor) => {
      // Common cursor styles that iframes and video players might use
      const iframeCursors = [
        'pointer', 'default', 'auto', 'grab', 'grabbing', 
        'text', 'move', 'crosshair', 'help', 'wait',
        'progress', 'copy', 'alias', 'cell', 'vertical-text',
        'all-scroll', 'col-resize', 'row-resize', 'n-resize',
        'e-resize', 's-resize', 'w-resize', 'ne-resize',
        'nw-resize', 'se-resize', 'sw-resize', 'ew-resize',
        'ns-resize', 'nesw-resize', 'nwse-resize', 'zoom-in',
        'zoom-out', 'grab', 'grabbing'
      ];
      return iframeCursors.includes(cursor);
    };

    // Enhanced function to check if interaction is on video elements
    const handleVideoInteraction = (e) => {
      try {
        const target = e.target || e.currentTarget;
        
        // Check if the interaction is with video elements
        if (isVideoElement(target)) {
          // Reset all suspicious activity counters for video interactions
          if (window.cursorChangeCount !== undefined) {
            window.cursorChangeCount = 0;
          }
          cursorChangeCount = 0;
          suspiciousActivity = 0;
          
          // Set a flag to indicate we're in video interaction mode
          window.videoInteractionMode = true;
          window.lastVideoInteraction = Date.now();
          
          // Clear any existing timeout
          if (window.videoInteractionTimeout) {
            clearTimeout(window.videoInteractionTimeout);
          }
          
          // Set timeout to exit video interaction mode after 3 seconds of inactivity
          window.videoInteractionTimeout = setTimeout(() => {
            window.videoInteractionMode = false;
            window.lastVideoInteraction = null;
          }, 3000);
          
          return true; // Allow video interactions
        }
        
        // Check if we're still in video interaction mode (recent video interaction)
        if (window.videoInteractionMode && window.lastVideoInteraction) {
          const timeSinceLastVideoInteraction = Date.now() - window.lastVideoInteraction;
          if (timeSinceLastVideoInteraction < 3000) { // 3 seconds grace period
            return true; // Still allow interactions
          }
        }
        
        return false;
      } catch (error) {
        return false;
      }
    };

    // Named functions for event listeners
    let resizeCount = 0;
    let lastResizeTime = Date.now();
    
    const handleResize = () => {
      if (window.editModalOpen) return;
      const currentTime = Date.now();
      const timeDiff = currentTime - lastResizeTime;
      
      // If multiple rapid resizes (possible recording software)
      if (timeDiff < 100) {
        resizeCount++;
        if (resizeCount > 3) {
          showBlackScreen();
          return;
        }
      } else {
        resizeCount = 0;
      }
      
      lastResizeTime = currentTime;
      
      const widthGap = window.outerWidth - window.innerWidth;
      const heightGap = window.outerHeight - window.innerHeight;
      if (widthGap > 50 || heightGap > 50) {
        showBlackout();
      }
    };

    const handleMouseOver = (e) => {
      try {
        // Check for video interaction first
        if (handleVideoInteraction(e)) {
          return; // Skip protection for video interactions
        }
        
        // Skip protection if we're in video interaction mode or edit modal is open
        if (window.videoInteractionMode || window.editModalOpen) {
          return;
        }
        
        // Skip protection for Bootstrap tabs
        if (e.target.closest('[data-bs-toggle="tab"]')) {
          return;
        }
        
        const cursor = window.getComputedStyle(e.target).cursor;
        if (cursor !== 'auto' && cursor !== 'default' && cursor !== lastCursor) {
          // Skip if this is a normal iframe cursor change
          if (isIframeCursorChange(cursor)) {
            lastCursor = cursor;
            return;
          }

          cursorChangeCount++;
          if (cursorChangeCount > 8) { // Increased threshold for better video compatibility
            showBlackout();
          }
          lastCursor = cursor;
        }
      } catch (error) {
        // Ignore errors
      }
    };

    const handleVisibilityChange = () => {
      // Skip protection if we're in video interaction mode or edit modal is open
      if (window.videoInteractionMode || window.editModalOpen) {
        return;
      }
      
      if (document.hidden) {
        showBlackScreen();
      }
    };

    const handleClick = (e) => {
      // Allow video interactions
      if (handleVideoInteraction(e)) {
        return;
      }
    };

    const handleMouseEnter = (e) => {
      // Allow video interactions
      if (handleVideoInteraction(e)) {
        return;
      }
    };

    const handleMouseMove = (e) => {
      // Allow video interactions
      if (handleVideoInteraction(e)) {
        return;
      }
    };

    document.addEventListener("keydown", handleKeyDown); // Add keydown listener
    document.addEventListener("keyup", handleKeyUp); // Add keyup listener
    document.addEventListener("visibilitychange", handleVisibilityChange);
    document.addEventListener("contextmenu", handleContextMenu); // Add context menu protection
    document.addEventListener("mousedown", handleMouseDown); // Add mouse down protection

    window.onblur = () => {
      // Skip protection if we're in video interaction mode or edit modal is open
      if (window.videoInteractionMode || window.editModalOpen) {
        return;
      }
      
      // Add a small delay to avoid false positives when clicking on video controls
      setTimeout(() => {
        if (!window.videoInteractionMode && !window.editModalOpen && !document.hasFocus()) {
          showBlackScreen();
        }
      }, 500);
    };

    const image = new Image();
    Object.defineProperty(image, 'id', {
      get() {
        showBlackout();
      }
    });

    window.addEventListener("resize", handleResize);

    // Enhanced cursor change detection
    let cursorChangeCount = 0;
    let lastCursor = '';
    let videoInteractionTimeout = null;
    
    const checkCursorChange = () => {
      try {
        // Skip protection if we're in video interaction mode or edit modal is open
        if (window.videoInteractionMode || window.editModalOpen) {
          return;
        }
        
        const currentCursor = window.getComputedStyle(document.body).cursor;
        if (currentCursor !== lastCursor && currentCursor !== 'auto' && currentCursor !== 'default') {
          // Skip if this is a normal iframe cursor change
          if (isIframeCursorChange(currentCursor)) {
            lastCursor = currentCursor;
            return;
          }
          
          cursorChangeCount++;
          if (cursorChangeCount > 10) { // Further increased threshold for better video compatibility
            showBlackout();
          }
          lastCursor = currentCursor;
        }
      } catch (error) {
        // Ignore errors
      }
    };

    // Check cursor changes less frequently
    const cursorChecker = setInterval(checkCursorChange, 200);

    // Add event listeners
    document.addEventListener('mouseover', handleMouseOver);
    document.addEventListener('click', handleClick);
    document.addEventListener('mouseenter', handleMouseEnter);
    document.addEventListener('mousemove', handleMouseMove);

    // Cleanup
    return () => {
      try {
        window.removeEventListener("resize", handleResize);
        clearInterval(cursorChecker);
        clearInterval(monitorInterval); // Clear monitoring interval
        document.removeEventListener('mouseover', handleMouseOver);
        document.removeEventListener("visibilitychange", handleVisibilityChange);
        document.removeEventListener('click', handleClick);
        document.removeEventListener('mouseenter', handleMouseEnter);
        document.removeEventListener('mousemove', handleMouseMove);
        window.onblur = null;
        if (videoInteractionTimeout) {
          clearTimeout(videoInteractionTimeout);
        }
        document.removeEventListener("keydown", handleKeyDown); // Remove keydown listener
        document.removeEventListener("keyup", handleKeyUp); // Remove keyup listener
        document.removeEventListener("contextmenu", handleContextMenu); // Remove context menu protection
        document.removeEventListener("mousedown", handleMouseDown); // Remove mouse down protection
      } catch (error) {
        // Ignore cleanup errors
      }
    };
  }, [showVideo]);

  // Disable protection system when edit modal is open
  useEffect(() => {
    if (showEditModal) {
      // Disable all protection systems when edit modal is open
      window.videoInteractionMode = true;
      window.editModalOpen = true;
      
      // Cleanup function to restore protection when modal closes
      return () => {
        window.videoInteractionMode = false;
        window.editModalOpen = false;
      };
    }
  }, [showEditModal]);

  useEffect(() => {
    window.scrollTo(0, 0);

    // Reset lesson details when component mounts
    dispatch(resetLessonDetails());

    // Fetch lesson details
    if (id) {
      dispatch(getLessonDetails(id));
      // Fetch lesson duties
      dispatch(fetchLessonDuties(id));
    }

    // Cleanup function to handle navigation properly
    return () => {
      // Clear any pending operations when component unmounts
      dispatch(resetLessonDetails());
    };
  }, [dispatch, id]);

  // Initialize edit form when lesson details are loaded
  useEffect(() => {
    if (lessonDetails && isTeacher) {
      // تحديد video_type للعرض بناءً على free_status
      let displayVideoType = lessonDetails.video_type || 'offline';
      if (lessonDetails.free_status === 1) {
        displayVideoType = 'free';
      }
      
      setEditForm({
        title_ar: lessonDetails.title_ar || lessonDetails.title || '',
        title_en: lessonDetails.title_en || '',
        description_ar: lessonDetails.description_ar || lessonDetails.description || '',
        description_en: lessonDetails.description_en || '',
        video_link: lessonDetails.video_link || '',
        video_type: displayVideoType,
        free_status: lessonDetails.free_status || 0,
        day: lessonDetails.day || '',
        time: lessonDetails.time || '',
        image: null,
        files: lessonDetails.files || []
      });
    }
  }, [lessonDetails, isTeacher]);

 

  // Handle rating submission effects
  useEffect(() => {
    if (submitError && userRole !== "parent") {
      toast.error(submitError);
      dispatch(clearRatingError());
    } else if (submitError && userRole === "parent") {
      // إخفاء رسالة الخطأ لولي الأمر
      dispatch(clearRatingError());
    }
  }, [submitError, dispatch, userRole]);

  useEffect(() => {
    if (submitSuccess) {
      toast.success("تم إرسال التقييم بنجاح");
      setRating(0);
      setComment('');
      dispatch(clearRatingSuccess());
      // Refresh lesson details to show the new rating
      dispatch(getLessonDetails(id));
    }
  }, [submitSuccess, dispatch, id]);

  // Handle duties error
  useEffect(() => {
    if (dutiesError && userRole !== "parent") {
      toast.error(dutiesError);
      dispatch(clearDutiesError());
    } else if (dutiesError && userRole === "parent") {
      // إخفاء رسالة الخطأ لولي الأمر
      dispatch(clearDutiesError());
    }
  }, [dutiesError, dispatch, userRole]);

  // Handle subscription effects
  useEffect(() => {
    if (subscriptionError && userRole !== "parent") {
      toast.error(subscriptionError);
      dispatch(clearSubscriptionError());
    } else if (subscriptionError && userRole === "parent") {
      // إخفاء رسالة الخطأ لولي الأمر
      dispatch(clearSubscriptionError());
    }
  }, [subscriptionError, dispatch, userRole]);

  useEffect(() => {
    if (subscriptionSuccess && paymentUrl) {
      // Redirect to payment URL in same window
      window.location.href = paymentUrl;
      dispatch(clearSubscriptionSuccess());
    }
  }, [subscriptionSuccess, paymentUrl, dispatch]);

  // Function to handle different video URL types
  const getVideoUrl = (url) => {
    if (!url) return '';

    // Check if it's a YouTube URL
    const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const youtubeMatch = url.match(youtubeRegex);

    if (youtubeMatch) {
      // Create a completely protected iframe URL with all controls disabled
      const videoId = youtubeMatch[1];
      const protectedUrl = `https://www.youtube.com/embed/${videoId}?` +
        'rel=0' + // No related videos
        '&modestbranding=1' + // Minimal YouTube branding
        '&showinfo=0' + // No video info
        '&controls=1' + // Show basic controls (play/pause)
        '&disablekb=1' + // Disable keyboard controls
        '&fs=0' + // Disable fullscreen
        '&iv_load_policy=3' + // Disable annotations
        '&cc_load_policy=0' + // Disable captions
        '&autoplay=1' + // Enable autoplay
        '&loop=0' + // No loop
        '&playlist=' + videoId + // Required for loop
        '&enablejsapi=0' + // Disable JavaScript API
        '&origin=' + encodeURIComponent(window.location.origin) + // Set origin
        '&widget_referrer=' + encodeURIComponent(window.location.origin) + // Set referrer
        '&autohide=1' + // Hide controls when not hovering
        '&color=white' + // White progress bar
        '&theme=dark' + // Dark theme
        '&playsinline=1' + // Play inline on mobile
        '&start=0' + // Start from beginning
        '&end=0' + // No end time
        '&vq=hd720' + // HD quality
        '&wmode=transparent' + // Transparent background
        '&allowfullscreen=false' + // Disable fullscreen
        '&allowscriptaccess=never' + // No script access
        '&allowtransparency=true' + // Allow transparency
        '&frameborder=0' + // No border
        '&scrolling=no' + // No scrolling
        '&marginwidth=0' + // No margin
        '&marginheight=0' + // No margin
        '&hspace=0' + // No horizontal space
        '&vspace=0' + // No vertical space
        '&align=middle' + // Center align
        '&valign=middle' + // Center align
        '&bgcolor=000000' + // Black background
        '&link=0' + // No links
        '&allow=autoplay; encrypted-media; picture-in-picture' + // Minimal permissions
        '&sandbox=allow-scripts allow-same-origin allow-presentation' + // Minimal sandbox
        '&loading=lazy' + // Lazy loading
        '&importance=low' + // Low importance
        '&referrerpolicy=no-referrer' + // No referrer
        '&crossorigin=anonymous' + // Anonymous cross-origin
        '&integrity=sha384-'; // Add integrity check

      return protectedUrl;
    }

    // Check if it's a Gizzi URL
    if (url.includes('gizzi.com') || url.includes('gizzi.app')) {
      // Return Gizzi URL as is - it will be handled by the iframe display logic
      return url;
    }

    // Check if it's a Jitsi URL
    if (url.includes('meet.jit.si') || url.includes('jitsi.org')) {
      // For Jitsi URLs, we need to add some parameters for better iframe embedding
      const jitsiUrl = new URL(url);
      jitsiUrl.searchParams.set('jitsi_meet_external_api_url', '');
      jitsiUrl.searchParams.set('config.prejoinPageEnabled', 'false');
      jitsiUrl.searchParams.set('config.startWithAudioMuted', 'true');
      jitsiUrl.searchParams.set('config.startWithVideoMuted', 'true');
      jitsiUrl.searchParams.set('config.disableModeratorIndicator', 'true');
      jitsiUrl.searchParams.set('config.disableReactions', 'true');
      jitsiUrl.searchParams.set('config.disablePolls', 'true');
      jitsiUrl.searchParams.set('config.disableSelfView', 'false');
      jitsiUrl.searchParams.set('config.disableSelfViewSettings', 'false');
      jitsiUrl.searchParams.set('config.disableLocalVideoFlip', 'true');
      jitsiUrl.searchParams.set('config.disableAudioLevels', 'false');
      jitsiUrl.searchParams.set('config.disableSimulcast', 'false');
      jitsiUrl.searchParams.set('config.enableLayerSuspension', 'true');
      jitsiUrl.searchParams.set('config.enableRemb', 'true');
      jitsiUrl.searchParams.set('config.enableTcc', 'true');
      jitsiUrl.searchParams.set('config.openBridgeChannel', 'websocket');
      jitsiUrl.searchParams.set('config.clientHeight', '100%');
      jitsiUrl.searchParams.set('config.clientWidth', '100%');
      return jitsiUrl.toString();
    }

    // Check if it's a direct video file (mp4, webm, etc.)
    const videoFileRegex = /\.(mp4|webm|ogg|mov|avi)$/i;
    if (videoFileRegex.test(url)) {
      return url;
    }

    // For other URLs, return the original URL to embed in iframe
    return url;
  };

  // Function to check if URL is VdoCipher
  const isVdoCipherUrl = (url) => {
    return url && (url.includes('vdocipher.com') || url.includes('player.vdocipher.com'));
  };

  // إنشاء رابط Jitsi للحصص الأونلاين
  const generateJitsiLink = () => {
    const roomName = `lesson_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    return `https://meet.jit.si/${roomName}`;
  };

  // Function to check if URL can be embedded in iframe
  const canEmbedInIframe = (url) => {
    if (!url) return false;

    // YouTube URLs can be embedded
    const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    if (youtubeRegex.test(url)) {
      return true;
    }

    // VdoCipher URLs can be embedded
    if (url.includes('vdocipher.com') || url.includes('player.vdocipher.com')) {
      return true;
    }

    // Gizzi URLs can be embedded
    if (url.includes('gizzi.com') || url.includes('gizzi.app')) {
      return true;
    }

    // Jitsi URLs can be embedded
    if (url.includes('meet.jit.si') || url.includes('jitsi.org')) {
      return true;
    }

    // Direct video files can be embedded
    const videoFileRegex = /\.(mp4|webm|ogg|mov|avi)$/i;
    if (videoFileRegex.test(url)) {
      return true;
    }

    // All other URLs can be embedded in iframe
    return true;
  };

  // Function to handle video play
  const handleVideoPlay = (e) => {
    e.stopPropagation();
    
    // Reset cursor change count for video interactions
    if (window.cursorChangeCount !== undefined) {
      window.cursorChangeCount = 0;
    }
    
    // Add a small delay to ensure the click is registered
    setTimeout(() => {
    // Check if user is subscribed (except for teachers)
    if (lessonDetails.subscriped === 0 && !isTeacher) {
      if (userRole === "parent") {
        toast.error("الابن غير مشترك في هذه الحصة");
      } else {
        toast.error("أنت لست مشترك في أي باقة");
      }
      return;
    }

    if (lessonDetails.video_link) {
      if (canEmbedInIframe(lessonDetails.video_link)) {
        // يمكن عرضه في iframe
        const videoUrl = getVideoUrl(lessonDetails.video_link);
        setVideoUrl(videoUrl);
        setShowVideo(true);
          
          // Scroll to video container on mobile
          if (window.innerWidth <= 768) {
            setTimeout(() => {
              const videoContainer = document.querySelector('.video-iframe-container');
              if (videoContainer) {
                videoContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }, 200);
          }
      } else {
        // فتح الرابط في نافذة جديدة (لروابط جيتسي، زوم، إلخ)
        window.open(lessonDetails.video_link, '_blank');
      }
    }
    }, 50);
  };

  // Function to handle rating submission
  const handleRatingSubmit = (e) => {
    e.preventDefault();

    if (rating === 0) {
      toast.error("يرجى اختيار تقييم");
      return;
    }

    // Comment is now optional
    const payload = {
      rate: rating,
      lesson_id: parseInt(id),
      comment: comment.trim() || "لا يوجد تعليق"
    };

    dispatch(submitLessonRating(payload));
  };

  // Function to handle file download
  const handleFileDownload = (e) => {
    // Check if user is subscribed (except for teachers)
    if (lessonDetails.subscriped === 0 && !isTeacher) {
      e.preventDefault();
      if (userRole === "parent") {
        toast.error("الابن غير مشترك في هذه الحصة");
      } else {
        toast.error("أنت لست مشترك في أي باقة");
      }
      return;
    }
  };

  // Function to handle video events for automatic state management
  const handleVideoEvents = () => {
    const videoElement = document.querySelector('.video-file-wrapper video');
    if (videoElement) {
      // Remove existing listeners to avoid duplicates
      videoElement.removeEventListener('play', () => {});
      videoElement.removeEventListener('pause', () => {});
      videoElement.removeEventListener('ended', () => {});
      
      // Add new listeners (empty functions since we don't need state management)
      videoElement.addEventListener('play', () => {});
      videoElement.addEventListener('pause', () => {});
      videoElement.addEventListener('ended', () => {});
      videoElement.addEventListener('error', () => {});
    }
  };

  // Set up video event listeners when video is shown
  useEffect(() => {
    if (showVideo) {
      // Small delay to ensure video element is rendered
      setTimeout(handleVideoEvents, 100);
    }
  }, [showVideo]);

  // Function to handle iframe click - prevent blackout
  const handleIframeClick = (e) => {
    // Set video interaction mode directly
    window.videoInteractionMode = true;
    window.lastVideoInteraction = Date.now();
    
    // Clear any existing timeout
    if (window.videoInteractionTimeout) {
      clearTimeout(window.videoInteractionTimeout);
    }
    
    // Set timeout to exit video interaction mode after 3 seconds of inactivity
    window.videoInteractionTimeout = setTimeout(() => {
      window.videoInteractionMode = false;
      window.lastVideoInteraction = null;
    }, 3000);
    
    // Reset activity timer when iframe is clicked
    if (window.lastActivityTime) {
      window.lastActivityTime = Date.now();
    }
    // Reset cursor change count for video interactions
    if (window.cursorChangeCount !== undefined) {
      window.cursorChangeCount = 0;
    }
  };

  // Function to handle video container click - prevent blackout
  const handleVideoContainerClick = (e) => {
    // Set video interaction mode directly
    window.videoInteractionMode = true;
    window.lastVideoInteraction = Date.now();
    
    // Clear any existing timeout
    if (window.videoInteractionTimeout) {
      clearTimeout(window.videoInteractionTimeout);
    }
    
    // Set timeout to exit video interaction mode after 3 seconds of inactivity
    window.videoInteractionTimeout = setTimeout(() => {
      window.videoInteractionMode = false;
      window.lastVideoInteraction = null;
    }, 3000);
    
    // Reset activity timer when video container is clicked
    if (window.lastActivityTime) {
      window.lastActivityTime = Date.now();
    }
    // Reset cursor change count for video interactions
    if (window.cursorChangeCount !== undefined) {
      window.cursorChangeCount = 0;
    }
  };

  // Function to handle video preview for non-subscribed users
  const handleVideoPreview = (e) => {
    // Prevent default behavior
    e.preventDefault();
    e.stopPropagation();
    
    // Reset cursor change count for video interactions
    if (window.cursorChangeCount !== undefined) {
      window.cursorChangeCount = 0;
    }
    
    // Add a small delay to ensure the click is registered
    setTimeout(() => {
    // Check if user is subscribed (except for teachers)
    if (lessonDetails.subscriped === 0 && !isTeacher) {
      toast.error("أنت لست مشترك في أي باقة");
      return;
    }

    if (lessonDetails.video_link) {
      if (canEmbedInIframe(lessonDetails.video_link)) {
        // يمكن عرضه في iframe
        const videoUrl = getVideoUrl(lessonDetails.video_link);
        setVideoUrl(videoUrl);
        setShowVideo(true);
          
          // Scroll to video container on mobile
          if (window.innerWidth <= 768) {
            setTimeout(() => {
              const videoContainer = document.querySelector('.video-iframe-container');
              if (videoContainer) {
                videoContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }, 200);
          }
      } else {
        // فتح الرابط في نافذة جديدة (لروابط جيتسي، زوم، إلخ)
        window.open(lessonDetails.video_link, '_blank');
      }
    }
    }, 50);
  };

  // Function to handle subscription
  const handleSubscription = (type) => {
    const subscriptionData = {
      subject_id: lessonDetails.subject_id,
      teacher_id: lessonDetails.user_id,
      type: type
    };

    dispatch(subscribeWithTeacher(subscriptionData));
  };

  // Function to handle zoom subscription
  const handleZoomSubscription = () => {
    const subscriptionData = {
      subject_id: lessonDetails.subject_id,
      teacher_id: lessonDetails.user_id,
      type: 'zoom'
    };

    dispatch(subscribeWithTeacher(subscriptionData));
  };

  // Function to handle edit form changes
  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'video_type') {
      if (value === 'free') {
        // إذا اختار "مجاني"، تعيين free_status = 1 و video_type = 'free' (للعرض)
        setEditForm(prev => ({
          ...prev,
          video_type: 'free',
          free_status: 1
        }));
      } else {
        // إذا اختار أي شيء آخر، تعيين free_status = 0
        setEditForm(prev => ({
          ...prev,
          video_type: value,
          free_status: 0
        }));
      }
    } else {
      setEditForm(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Function to handle file upload
  const handleFileUpload = (e, index) => {
    const file = e.target.files[0];
    if (file) {
      setEditForm(prev => ({
        ...prev,
        files: prev.files.map((f, i) => 
          i === index ? { ...f, file: file } : f
        )
      }));
    }
  };

  // Function to handle image upload
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditForm(prev => ({
        ...prev,
        image: file
      }));
    }
  };

  // Function to add new file
  const addNewFile = () => {
    setEditForm(prev => ({
      ...prev,
      files: [...prev.files, { title_ar: '', title_en: '', file: null }]
    }));
  };

  // Function to remove file
  const removeFile = (index) => {
    setEditForm(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index)
    }));
  };

  // Function to handle file deletion
  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا الملف؟')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token3almny');
      const response = await fetch(`https://newalemny.3almny.app/api/lessons/delete_lesson_file/${fileId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });
      
      let data = {};
      let responseText = '';
      try {
        responseText = await response.text();
        if (responseText.trim()) {
          data = JSON.parse(responseText);
        }
      } catch (jsonError) {
        console.warn('Failed to parse response as JSON:', responseText);
      }
      
      if (response.ok) {
        if (userRole !== "parent") {
          toast.success('تم حذف الملف بنجاح');
        }
        // Refresh lesson details
        dispatch(getLessonDetails(id));
      } else {
        if (userRole !== "parent") {
          toast.error(data.message || 'حدث خطأ في حذف الملف');
        }
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      if (userRole !== "parent") {
        toast.error('حدث خطأ في الاتصال بالخادم');
      }
    }
  };

  // Function to handle edit form submission
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    if (isSubmittingEdit) return; // Prevent multiple submissions
    
    setIsSubmittingEdit(true);
    
    try {
      const formData = new FormData();
      
      // Add basic fields
      formData.append('subject_id', lessonDetails.subject_id);
      formData.append('day', editForm.day);
      formData.append('time', editForm.time);
      
      // إضافة رابط الفيديو بناءً على نوع الفيديو
      if (editForm.video_type === 'online') {
        // للحصص الأونلاين: إنشاء رابط Jitsi تلقائياً
        const jitsiLink = generateJitsiLink();
        formData.append('video_link', jitsiLink);
      } else {
        // للحصص المسجلة: إرسال الرابط المدخل يدوياً
        if (editForm.video_link) {
          formData.append('video_link', editForm.video_link);
        }
      }
      
      formData.append('title_ar', editForm.title_ar);
      formData.append('title_en', editForm.title_en);
      formData.append('description_ar', editForm.description_ar);
      formData.append('description_en', editForm.description_en);
      
      // إرسال video_type و free_status
      if (editForm.video_type === 'free') {
        // إذا كان مجاني، إرسال offline مع free_status = 1
        formData.append('video_type', 'offline');
        formData.append('free_status', 1);
      } else {
        // إذا كان مسجل أو أونلاين، إرسال القيم كما هي
        formData.append('video_type', editForm.video_type);
        formData.append('free_status', editForm.free_status);
      }
      
      // Add image if selected
      if (editForm.image) {
        formData.append('image', editForm.image);
      }
      
      // Add files
      editForm.files.forEach((file, index) => {
        if (file.title_ar) formData.append(`files[${index}][title_ar]`, file.title_ar);
        if (file.title_en) formData.append(`files[${index}][title_en]`, file.title_en);
        if (file.file) formData.append(`files[${index}][file]`, file.file);
      });
      
      const token = localStorage.getItem('token3almny');
      const response = await fetch(`https://newalemny.3almny.app/api/lessons/update_one/${id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData
      });
      
      let data = {};
      let responseText = '';
      try {
        responseText = await response.text();
        if (responseText.trim()) {
          data = JSON.parse(responseText);
        }
      } catch (jsonError) {
        console.warn('Failed to parse response as JSON:', responseText);
      }
      
      if (response.ok) {
        if (userRole !== "parent") {
          toast.success('تم تحديث الحصة بنجاح');
        }
        setShowEditModal(false);
        // Refresh lesson details
        dispatch(getLessonDetails(id));
      } else {
        if (userRole !== "parent") {
          toast.error(data.message || 'حدث خطأ في تحديث الحصة');
        }
      }
    } catch (error) {
      if (userRole !== "parent") {
        toast.error('حدث خطأ في الاتصال بالخادم');
      }
    } finally {
      setIsSubmittingEdit(false);
    }
  };

  if (lessonDetailsLoading) {
    return <div className="loading">جاري التحميل...</div>;
  }

  if (!lessonDetails) {
    return <div className="no-lesson">لا توجد بيانات للحصة</div>;
  }

  return (
    <div className={`lesson-details-page ${isDarkMode ? "dark-mode" : "light-mode"}`}>
      {/* Watermark for Students */}
      {isStudent && currentUser && (
        <div className="student-watermark">
          <div className="watermark-content">
            <div className="student-avatar">
              <img 
                src={profileData?.prof_img || currentUser.image || "/user.png"} 
                alt="Student Avatar"
                className="student-image"
                onError={(e) => {
                  e.target.src = "/user.png";
                }}
              />
            </div>
            <div className="student-info">
              <span className="student-name">{profileData?.name || currentUser.name || currentUser.username || "طالب"}</span>
              <span className="student-code">كود الطالب: {profileData?.stud_code || currentUser.stud_code || currentUser.id || "000"}</span>
              {profileData?.email && (
                <span className="student-email">{profileData.email}</span>
              )}
            </div>
          </div>
        </div>
      )}
      
      <div className="container">

        {/* Back to Home Button */}
        <div className="back-to-home-container">
          <button 
            className="btn btn-outline-primary back-to-home-btn"
            onClick={() => {
              dispatch(resetLessonDetails());
              navigate('/');
            }}
          >
            <FaArrowRight className="me-2" />
            العودة للصفحة الرئيسية
          </button>
        </div>

        {/* Breadcrumb */}
        <div className="lessons-breadcrumb">
          <h1 className="breadcrumb-title">تفاصيل الحصة</h1>
          <div className="breadcrumb-links2">
            <span 
              className="breadcrumb-link" 
              style={{ cursor: 'pointer' }}
              onClick={() => {
                dispatch(resetLessonDetails());
                navigate('/');
              }}
            >
              الرئيسية
            </span>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-current">تفاصيل الحصة</span>
          </div>
        </div>

        {/* Edit Button for Teachers */}
        {isTeacher && (
          <div className="edit-lesson-btn-container">
            <button 
              className="btn btn-primary edit-lesson-btn"
              onClick={() => setShowEditModal(true)}
            >
              <FaEdit /> تعديل الحصة
            </button>
          </div>
        )}

        {/* Main content */}
        <div className="lesson-content row mt-4">
          {/* Video Section - Full Width */}
          {showVideo && videoUrl && (
            <div className="col-12 mb-4">
              <div className="video-section">
                <div 
                  className="video-iframe-container"
                  onClick={handleVideoContainerClick}
                  onMouseEnter={handleVideoContainerClick}
                >
                  {videoUrl.startsWith('http') && !videoUrl.includes('youtube.com/embed') && !isVdoCipherUrl(videoUrl) && !videoUrl.includes('gizzi.com') && !videoUrl.includes('gizzi.app') && !videoUrl.includes('meet.jit.si') ? (
                    // Direct video file
                    <div className="youtube-iframe-wrapper" onClick={handleVideoContainerClick}>
                      <video
                        src={videoUrl}
                        controls
                        autoPlay
                        className="video-iframe"
                        onContextMenu={(e) => e.preventDefault()}
                        onDragStart={(e) => e.preventDefault()}
                        onClick={handleIframeClick}
                        onMouseEnter={handleIframeClick}
                        onMouseMove={handleIframeClick}
                        onMouseOver={handleIframeClick}
                      >
                        Your browser does not support the video tag.
                      </video>
                      <div className="youtube-iframe-overlay"></div>
                    </div>
                  ) : (
                    // YouTube, Gizzi, Jitsi, VdoCipher or other embeddable content with enhanced protection
                    <div className="youtube-iframe-wrapper" onClick={handleVideoContainerClick}>
                      <iframe
                        src={videoUrl}
                        title="Lesson Video"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen; microphone; camera"
                        allowFullScreen
                        className="video-iframe"
                        sandbox="allow-scripts allow-same-origin allow-presentation allow-forms allow-popups allow-modals"
                        onContextMenu={(e) => e.preventDefault()}
                        onDragStart={(e) => e.preventDefault()}
                        onClick={handleIframeClick}
                        onMouseEnter={handleIframeClick}
                        onMouseMove={handleIframeClick}
                        onMouseOver={handleIframeClick}
                      ></iframe>
                      <div className="youtube-iframe-overlay"></div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Lesson Preview Image */}
          {!showVideo && (
            <div className="col-12 mb-4">
              <div className="lesson-video">
                <img src={'/lessonDetails.png'} className="img-fluid rounded border border-primary" alt="lesson" />
                <div className="lesson-video-overlay" onClick={handleVideoContainerClick}>
                  <div 
                    className="video-play-icon" 
                    onClick={handleVideoPlay}
                    onTouchStart={(e) => e.preventDefault()}
                    onTouchEnd={(e) => e.preventDefault()}
                    onMouseEnter={handleVideoContainerClick}
                    style={{ cursor: 'pointer' }}
                  >
                    <FaPlay />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Main Details */}
          <div className="col-md-8 lesson-main">
            <div className="lesson-meta d-flex align-items-center justify-content-between mb-3 flex-wrap">
              <div className="d-flex align-items-center gap-2">
                <img src="/user.png" className="rounded-circle me-2" alt="teacher" />
                <div>
                  <div className="lesson-teacher-name fw-bold">{lessonDetails.user_name}</div>
                  <div className="lesson-subject text-primary">{lessonDetails.subject_name}</div>
                </div>
              </div>
              <div className="lesson-rating text-end">
                <div>Review</div>
                <div className="text-warning">⭐️⭐️⭐️⭐️⭐️ ({lessonDetails.rate || 0})</div>
              </div>
            </div>

            <h4 className="fw-bold lesson-title">{lessonDetails.title}</h4>

            {/* Tabs */}
            <ul className="nav nav-tabs mt-4" role="tablist">
              <li className="nav-item">
                <button className="nav-link active" data-bs-toggle="tab" data-bs-target="#desc" type="button">وصف الحصة</button>
              </li>
              <li className="nav-item">
                <button className="nav-link" data-bs-toggle="tab" data-bs-target="#comments" type="button">الملفات</button>
              </li>
              <li className="nav-item">
                <button className="nav-link" data-bs-toggle="tab" data-bs-target="#reviews" type="button">التقييمات</button>
              </li>
              {/* إخفاء تاب الواجب لولي الأمر */}
              {userRole !== "parent" && (
                <li className="nav-item">
                  <button className="nav-link" data-bs-toggle="tab" data-bs-target="#duties" type="button">الواجب</button>
                </li>
              )}
            </ul>

            <div className="tab-content mt-4" id="lessonTabContent">
              {/* وصف الحصة */}
              <div className="tab-pane fade show active" id="desc" role="tabpanel">
                <div className="lesson-description">
                  <p>{lessonDetails.description}</p>
                  {lessonDetails.about && <p>{lessonDetails.about}</p>}
                </div>
              </div>

              {/* الملفات */}
              <div className="tab-pane fade" id="comments" role="tabpanel">
                <div className="lesson-files">
                  {lessonDetails.subscriped === 0 && userRole === "parent" ? (
                    <div className="text-center py-4">
                      <div className="alert alert-warning">
                        <h5>⚠️ الابن غير مشترك</h5>
                        <p>لا يمكن عرض الملفات لأن الابن غير مشترك في هذه الحصة</p>
                      </div>
                    </div>
                  ) : lessonDetails.files && lessonDetails.files.length > 0 ? (
                    lessonDetails.files.map((file, index) => (
                      <div className="file-item" key={index}>
                        <div className="file-icon">
                          <img src={'/Filetype.png'} style={{ width: '40px', height: '40px' }} alt="file-icon" />
                        </div>
                        <div className="file-info">
                          <div className="file-name">{file.title}</div>
                        </div>
                        <div className="file-actions">
                          <a
                            href={file.file}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-primary"
                            onClick={handleFileDownload}
                          >
                            تحميل
                          </a>
                          {isTeacher && (
                            <button
                              className="btn btn-sm btn-danger delete-file-btn"
                              onClick={() => handleDeleteFile(file.id)}
                              title="حذف الملف"
                            >
                              <FaTrash />
                            </button>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4">
                      <p>لا توجد ملفات مرفقة</p>
                    </div>
                  )}
                </div>
              </div>

              {/* التقييمات */}
              <div className="tab-pane fade" id="reviews" role="tabpanel">
                <div className="lesson-reviews">
                  {lessonDetails.subscriped === 0 && userRole === "parent" ? (
                    <div className="text-center py-4">
                      <div className="alert alert-warning">
                        <h5>⚠️ الابن غير مشترك</h5>
                        <p>لا يمكن عرض التقييمات لأن الابن غير مشترك في هذه الحصة</p>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Previous Reviews */}
                      <div className="previous-reviews mb-4">
                        <h5 className="mb-3">التقييمات</h5>
                        {lessonDetails.rates && lessonDetails.rates.length > 0 ? (
                          lessonDetails.rates.map((review, i) => (
                            <div className="review-box d-flex align-items-start mb-4" key={i}>
                              <div className="review-avatar rounded-circle me-3">
                                <img
                                  src={review.user_image || "/user.png"}
                                  alt="user"
                                  className="img-fluid rounded-circle"
                                  onError={(e) => {
                                    e.target.src = "/user.png";
                                  }}
                                />
                              </div>
                              <div className="review-content w-100">
                                <div className="d-flex justify-content-between">
                                  <strong>{review.user_name || "مستخدم"}</strong>
                                  <small className="text-muted">{review.day || "تاريخ غير محدد"}</small>
                                </div>
                                <div className="text-warning my-1">
                                  {"⭐".repeat(review.rate || 0)}
                                </div>
                                <p className="mb-1 review-text">
                                  {review.comment || "لا يوجد تعليق"}
                                </p>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-4">
                            <p>لا توجد تقييمات بعد</p>
                          </div>
                        )}
                      </div>

                      <hr className="my-4" />

                      {/* Rating Form */}
                      <div className="rating-form">
                        <h5 className="mb-3">أضف تقييمك</h5>
                        <form onSubmit={handleRatingSubmit}>
                          <div className="mb-3">
                            <div className="rating-stars">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <button
                                  key={star}
                                  type="button"
                                  className={`star-btn ${rating >= star ? 'active' : ''}`}
                                  onClick={() => setRating(star)}
                                  title={`${star} نجمة`}
                                >
                                  <FaStar />
                                </button>
                              ))}
                            </div>
                            {rating > 0 && (
                              <div className=" mt-2">
                                <small className="text-muted">
                                  {rating === 1 && "تقييم ضعيف"}
                                  {rating === 2 && "تقييم مقبول"}
                                  {rating === 3 && "تقييم جيد"}
                                  {rating === 4 && "تقييم ممتاز"}
                                  {rating === 5 && "تقييم رائع"}
                                </small>
                              </div>
                            )}
                          </div>
                          <div className="mb-3">
                            <label htmlFor="comment" className="form-label">التعليق:</label>
                            <textarea
                              id="comment"
                              className="form-control"
                              rows="4"
                              value={comment}
                              onChange={(e) => setComment(e.target.value)}
                              placeholder="اكتب تعليقك هنا... (اختياري)"
                            />
                          </div>
                          <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? "جاري الإرسال..." : "إرسال التقييم"}
                          </button>
                        </form>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* الواجب */}
              {userRole !== "parent" && (
                <div className="tab-pane fade" id="duties" role="tabpanel">
                  <div className="lesson-duties">
                    {lessonDetails.subscriped === 0 && userRole === "parent" ? (
                      <div className="text-center py-4">
                        <div className="alert alert-warning">
                          <h5>⚠️ الابن غير مشترك</h5>
                          <p>لا يمكن عرض الواجبات لأن الابن غير مشترك في هذه الحصة</p>
                        </div>
                      </div>
                    ) : dutiesLoading ? (
                      <div className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">جاري التحميل...</span>
                        </div>
                      </div>
                    ) : duties && duties.length > 0 ? (
                      <div className="duties-cards-grid">
                        {duties.map((duty, index) => (
                          <div className="duty-card" key={duty.id || index} onClick={() => {
                            if (isTeacher) {
                              // للمعلم: عرض تفاصيل الواجب
                              setShowDutyModal(true);
                              setSelectedDuty(duty);
                            } else if (userRole === "parent") {
                              // لولي الأمر: الانتقال لصفحة الواجب مع lesson_id و child_id
                              const selectedChild = localStorage.getItem('selectedChildId') || '62'; // افتراضي
                              navigate(`/duty-solver/${duty.id}?lessonId=${id}&childId=${selectedChild}`);
                            } else {
                              // للطالب: الانتقال لحل الواجب
                              navigate(`/duty-solver/${duty.id}?lessonId=${id}`);
                            }
                          }}>
                            <div className="duty-card-header">
                              <div className="duty-card-icon">
                                <FaBook />
                              </div>
                              <div className="duty-card-number">واجب {index + 1}</div>
                            </div>

                            <div className="duty-card-content">
                              <h5 className="duty-card-title">{duty.title}</h5>
                              <p className="duty-card-description">
                                {duty.description || "لا يوجد وصف للواجب"}
                              </p>

                              <div className="duty-card-questions">
                                <span className="questions-count">
                                  1 سؤال
                                </span>
                              </div>
                            </div>

                            <div className="duty-card-footer">
                              <div className="duty-card-status">
                                <span className="status-badge">متاح للحل</span>
                              </div>
                              <div className="duty-card-action">
                                <span className="action-text">
                                  {isTeacher ? "اضغط للعرض" : userRole === "parent" ? "اضغط للعرض" : "اضغط للحل"}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <FaBook className="text-muted mb-3" style={{ fontSize: '3rem' }} />
                        <p>لا توجد واجبات لهذه الحصة</p>
                      </div>
                    )}
                    
                    {/* أزرار الواجب للمعلمين */}
                    {isTeacher && (
                      <div className="text-center mt-4">
                        <div className="d-flex gap-3 justify-content-center flex-wrap">
                          <button
                            className="btn btn-primary"
                            onClick={() => navigate(`/create-duty/${id}`)}
                            style={{
                              background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                              border: 'none',
                              borderRadius: '12px',
                              padding: '0.75rem 1.5rem',
                              fontWeight: '600',
                              boxShadow: '0 4px 16px rgba(59, 130, 246, 0.3)',
                              transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.transform = 'translateY(-2px)';
                              e.target.style.boxShadow = '0 6px 20px rgba(59, 130, 246, 0.4)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.transform = 'translateY(0)';
                              e.target.style.boxShadow = '0 4px 16px rgba(59, 130, 246, 0.3)';
                            }}
                          >
                            <FaPlus className="me-2" />
                            إنشاء واجب جديد
                          </button>
                          
                          <button
                            className="btn btn-success"
                            onClick={() => navigate(`/duty-records/${id}`)}
                            style={{
                              background: 'linear-gradient(135deg, #10b981, #059669)',
                              border: 'none',
                              borderRadius: '12px',
                              padding: '0.75rem 1.5rem',
                              fontWeight: '600',
                              boxShadow: '0 4px 16px rgba(16, 185, 129, 0.3)',
                              transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.transform = 'translateY(-2px)';
                              e.target.style.boxShadow = '0 6px 20px rgba(16, 185, 129, 0.4)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.transform = 'translateY(0)';
                              e.target.style.boxShadow = '0 4px 16px rgba(16, 185, 129, 0.3)';
                            }}
                          >
                            <FaBook className="me-2" />
                            سجل الواجبات
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

          </div>
          {/* Sidebar */}
          <div className="col-md-4 lesson-sidebar">
            <div className="lesson-box p-4">
              {(lessonDetails.subscriped === 1 || isTeacher) ? (
                // المستخدم مشترك أو المعلم - عرض المحتوى
                <>
                  <h5 className="fw-bold mb-3">معلومات الحصة :</h5>
                  <ul className="list-unstyled lesson-includes">
                    <li className="d-flex align-items-center justify-content-between">
                      <span>الملفات:</span> 
                      <span className="badge bg-primary">{lessonDetails.files?.length || 0}</span>
                    </li>
                    <li className="d-flex align-items-center justify-content-between">
                      <span>التقييمات:</span> 
                      <span className="badge bg-success">{lessonDetails.rates?.length || 0}</span>
                    </li>
                    <li className="d-flex align-items-center justify-content-between">
                      <span>نوع الفيديو:</span> 
                      <span className="badge bg-info">
                        {lessonDetails.video_type === 'offline' ? 'مسجل' : 'اونلاين'}
                      </span>
                    </li>
                    {lessonDetails.day && (
                      <li className="d-flex align-items-center justify-content-between">
                        <span>التاريخ:</span> 
                        <span className="badge bg-secondary">{lessonDetails.day}</span>
                      </li>
                    )}
                    {lessonDetails.time && (
                      <li className="d-flex align-items-center justify-content-between">
                        <span>الوقت:</span> 
                        <span className="badge bg-secondary">{lessonDetails.time}</span>
                      </li>
                    )}
                  </ul>

                  {/* عرض الفيديو إذا كان متاح */}
                  {lessonDetails.video_link && (
                    <div className="mt-3">
                     <button
                          onClick={handleVideoPlay}
                          className="btn btn-primary w-100 mb-2"
                          style={{ 
                            cursor: 'pointer',
                            touchAction: 'manipulation',
                            WebkitTapHighlightColor: 'transparent'
                          }}
                        >
                        مشاهدة الفيديو
                      </button>
                    </div>
                  )}
                </>
              ) : (
                // المستخدم غير مشترك - عرض باقات الاشتراك (إلا للمعلمين وولي الأمر)
                !isTeacher && userRole !== "parent" ? (
                  <>
                    <h5 className="fw-bold mb-3">باقات الاشتراك :</h5>

                    {/* اشتراك زووم */}
                    {lessonDetails.has_zoom === 1 && (
                      <div className="subscription-option mb-3">
                        <h6>اشتراك زووم</h6>
                        <div className="price">{lessonDetails.zoom_price} جنيه مصري</div>
                        <button
                          className="btn btn-primary w-100 mb-2"
                          onClick={handleZoomSubscription}
                          disabled={isSubscribing}
                        >
                          {isSubscribing ? "جاري التحميل..." : "اشترك في زووم"}
                        </button>
                      </div>
                    )}

                    {/* اشتراك شهري بمشرف */}
                    <div className="subscription-option mb-3">
                      <h6>اشتراك شهري بمشرف</h6>
                      <div className="price">{lessonDetails.normal_price_with_super_monthly} جنيه مصري</div>
                      <button
                        className="btn btn-primary w-100 mb-2"
                        onClick={() => handleSubscription('normal_price_with_super_monthly')}
                        disabled={isSubscribing}
                      >
                        {isSubscribing ? "جاري التحميل..." : "اشترك شهري"}
                      </button>
                    </div>

                    {/* اشتراك شهري بدون مشرف */}
                    <div className="subscription-option mb-3">
                      <h6>اشتراك شهري بدون مشرف</h6>
                      <div className="price">{lessonDetails.normal_price_without_super_monthly} جنيه مصري</div>
                      <button
                        className="btn btn-primary w-100 mb-2"
                        onClick={() => handleSubscription('normal_price_without_super_monthly')}
                        disabled={isSubscribing}
                      >
                        {isSubscribing ? "جاري التحميل..." : "اشترك شهري"}
                      </button>
                    </div>

                    {/* اشتراك سنوي بمشرف */}
                    <div className="subscription-option mb-3">
                      <h6>اشتراك سنوي بمشرف</h6>
                      <div className="price">{lessonDetails.normal_price_with_super_yearly} جنيه مصري</div>
                      <button
                        className="btn btn-primary w-100 mb-2"
                        onClick={() => handleSubscription('normal_price_with_super_yearly')}
                        disabled={isSubscribing}
                      >
                        {isSubscribing ? "جاري التحميل..." : "اشترك سنوي"}
                      </button>
                    </div>

                    {/* اشتراك سنوي بدون مشرف */}
                    <div className="subscription-option mb-3">
                      <h6>اشتراك سنوي بدون مشرف</h6>
                      <div className="price">{lessonDetails.normal_price_without_super_yearly} جنيه مصري</div>
                      <button
                        className="btn btn-primary w-100 mb-2"
                        onClick={() => handleSubscription('normal_price_without_super_yearly')}
                        disabled={isSubscribing}
                      >
                        {isSubscribing ? "جاري التحميل..." : "اشترك سنوي"}
                      </button>
                    </div>

                    {/* عرض الفيديو كمعاينة إذا كان متاح */}
                  </>
                ) : userRole === "parent" ? (
                  // رسالة خاصة لولي الأمر
                  <div className="text-center py-4">
                    <div className="alert alert-info">
                      <h5>ℹ️ معلومات الحصة</h5>
                      <p>هذه الحصة متاحة للاشتراك. يمكنك التواصل مع إدارة الموقع لمعرفة كيفية اشتراك الابن.</p>
                    </div>
                  </div>
                ) : null
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="modal-overlay edit-modalContainer">
          <div className="modal-content">
            <div className="modal-header">
              <h5>تعديل الحصة</h5>
              <button 
                className="close-btn"
                onClick={() => setShowEditModal(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleEditSubmit}>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>عنوان الحصة (عربي)</label>
                      <input
                        type="text"
                        name="title_ar"
                        value={editForm.title_ar}
                        onChange={handleEditFormChange}
                        className="form-control"
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>عنوان الحصة (إنجليزي)</label>
                      <input
                        type="text"
                        name="title_en"
                        value={editForm.title_en}
                        onChange={handleEditFormChange}
                        className="form-control"
                      />
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>الوصف (عربي)</label>
                      <textarea
                        name="description_ar"
                        value={editForm.description_ar}
                        onChange={handleEditFormChange}
                        className="form-control"
                        rows="3"
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>الوصف (إنجليزي)</label>
                      <textarea
                        name="description_en"
                        value={editForm.description_en}
                        onChange={handleEditFormChange}
                        className="form-control"
                        rows="3"
                      />
                    </div>
            </div>
          </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>نوع الفيديو</label>
                      <select
                        name="video_type"
                        value={editForm.video_type}
                        onChange={handleEditFormChange}
                        className="form-control"
                      >
                        <option value="offline">مسجل</option>
                        <option value="online">اونلاين</option>
                        <option value="free">مجاني</option>
                      </select>
                    </div>
                  </div>
                  <div className="col-md-6">
                    {(editForm.video_type === 'offline' || editForm.free_status === 1) ? (
                      <div className="form-group">
                        <label>رابط الفيديو</label>
                        <input
                          type="url"
                          name="video_link"
                          value={editForm.video_link}
                          onChange={handleEditFormChange}
                          className="form-control"
                          placeholder={editForm.free_status === 1 ? "أدخل رابط الفيديو المجاني" : "أدخل رابط الفيديو المسجل"}
                        />
                      </div>
                    ) : (
                      <div className="form-group">
                        <label>رابط الحصة الأونلاين</label>
                        <div className="alert alert-info">
                          <i className="fas fa-info-circle me-2"></i>
                          سيتم إنشاء رابط Jitsi تلقائياً عند حفظ التعديلات
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>التاريخ</label>
                      <input
                        type="date"
                        name="day"
                        value={editForm.day}
                        onChange={handleEditFormChange}
                        className="form-control"
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>الوقت</label>
                      <input
                        type="time"
                        name="time"
                        value={editForm.time}
                        onChange={handleEditFormChange}
                        className="form-control"
                      />
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label>صورة الحصة</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="form-control"
                  />
                </div>

                <div className="form-group">
                  <label>الملفات</label>
                  {editForm.files.map((file, index) => (
                    <div key={index} className="file-input-group">
                      <div className="row">
                        <div className="col-md-4">
                          <input
                            type="text"
                            placeholder="عنوان الملف (عربي)"
                            value={file.title_ar || ''}
                            onChange={(e) => {
                              setEditForm(prev => ({
                                ...prev,
                                files: prev.files.map((f, i) => 
                                  i === index ? { ...f, title_ar: e.target.value } : f
                                )
                              }));
                            }}
                            className="form-control"
                          />
                        </div>
                        <div className="col-md-4">
                          <input
                            type="text"
                            placeholder="عنوان الملف (إنجليزي)"
                            value={file.title_en || ''}
                            onChange={(e) => {
                              setEditForm(prev => ({
                                ...prev,
                                files: prev.files.map((f, i) => 
                                  i === index ? { ...f, title_en: e.target.value } : f
                                )
                              }));
                            }}
                            className="form-control"
                          />
                        </div>
                        <div className="col-md-3">
                          <input
                            type="file"
                            accept=".pdf,.doc,.docx"
                            onChange={(e) => handleFileUpload(e, index)}
                            className="form-control"
                          />
                        </div>
                        <div className="col-md-1">
                          <button
                            type="button"
                            onClick={() => removeFile(index)}
                            className="btn btn-danger btn-sm"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addNewFile}
                    className="btn btn-secondary btn-sm mt-2"
                  >
                    + إضافة ملف
                  </button>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="btn btn-secondary"
                >
                  إلغاء
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSubmittingEdit}
                >
                  {isSubmittingEdit ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      جاري الحفظ...
                    </>
                  ) : (
                    'حفظ التعديلات'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* مودال عرض الواجب للمعلم */}
      {showDutyModal && selectedDuty && (
        <div className="modal-overlay edit-modalContainer" onClick={() => setShowDutyModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h4>عرض الواجب</h4>
              <button 
                className="modal-close-btn"
                onClick={() => setShowDutyModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="duty-preview">
                <div className="duty-title">
                  <h5>{selectedDuty.title}</h5>
                  {selectedDuty.description && (
                    <p className="duty-description">{selectedDuty.description}</p>
                  )}
                </div>
                
                {selectedDuty.answers && selectedDuty.answers.length > 0 && (
                  <div className="duty-answers">
                    <h6>تفاصيل الواجب:</h6>
                    <div className="answers-list">
                      {selectedDuty.answers.map((answer, index) => (
                        <div 
                          key={answer.id}
                          className={`answer-item ${answer.true_status === 1 ? 'correct-answer' : ''}`}
                        >
                          <span className="answer-letter">
                            {String.fromCharCode(65 + index)}
                          </span>
                          <span className="answer-text">{answer.answer}</span>
                          {answer.true_status === 1 && (
                            <span className="correct-badge">✓ الإجابة الصحيحة</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="duty-info">
                  <div className="info-item">
                    <span className="info-label">عدد الأسئلة:</span>
                    <span className="info-value">{selectedDuty.answers?.length || 1}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">حالة الواجب:</span>
                    <span className="info-value status-active">متاح للطلاب</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowDutyModal(false)}
              >
                إغلاق
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LessonDetails;